'use client';

import Image from 'next/image';
import { useEffect, useMemo, useRef, useState } from 'react';

export type GalleryItem = {
  id: string;
  url: string;
  altText?: string | null;
};

/**
 * ギャラリーカルーセル（クライアント）
 * 目的: 画像を「中央の1枚は全表示、左右は半分見切れ」でスライド表示（自動＋手動）
 * 仕様:
 *   - ユーザーの左右スクロールは不可（矢印/ドット/自動のみ）
 *   - 無限ループ（末尾→先頭 / 先頭→末尾）
 * 引数: items 画像配列
 */
export default function GalleryCarouselClient({
  items,
}: {
  items: GalleryItem[];
}) {
  const safeItems = useMemo(() => items.filter((x) => x.url), [items]);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const slideRefs = useRef<Array<HTMLDivElement | null>>([]);
  const rafRef = useRef<number | null>(null);
  const animatingRef = useRef(false);

  const len = safeItems.length;
  const hasLoop = len > 1;
  const CLONE_COUNT = 2; // peek表示で端が空白にならないよう、前後2枚ずつクローンを用意する

  // NOTE: 無限ループ用に「前後1枚ずつ」クローンを入れる
  const loopItems = useMemo(() => {
    if (!hasLoop) return safeItems;
    const head = safeItems.slice(0, CLONE_COUNT);
    const tail = safeItems.slice(Math.max(0, len - CLONE_COUNT));
    // 例: [last2,last1, ...real..., first1,first2]
    return [...tail, ...safeItems, ...head];
  }, [hasLoop, len, safeItems]);

  // current: loopItems のインデックス（0..len+2*CLONE_COUNT-1）
  // 初期は CLONE_COUNT（=最初の実アイテム）
  const [current, setCurrent] = useState<number>(hasLoop ? CLONE_COUNT : 0);
  const [enableTransition, setEnableTransition] = useState(true);
  const [translatePx, setTranslatePx] = useState(0);

  useEffect(() => {
    if (!hasLoop) return;
    const t = window.setInterval(() => {
      if (animatingRef.current) return;
      animatingRef.current = true;
      setCurrent((p) => p + 1);
    }, 4500);
    return () => window.clearInterval(t);
  }, [hasLoop]);

  // items の増減で current が不正にならないよう調整
  useEffect(() => {
    if (!hasLoop) {
      setCurrent(0);
      return;
    }
    // lenが変わった場合、基本は先頭に戻す（安全側）
    setEnableTransition(false);
    setCurrent(CLONE_COUNT);
  }, [hasLoop, len]);

  useEffect(() => {
    // 目的: transition off の直後に on に戻す（ジャンプ後の次操作でアニメする）
    if (enableTransition) return;
    const id = window.requestAnimationFrame(() => setEnableTransition(true));
    return () => window.cancelAnimationFrame(id);
  }, [enableTransition]);

  // 目的: 現在スライドが中央に来るように translate を計算する
  const measureAndSetTranslate = () => {
    const viewport = viewportRef.current;
    const el = slideRefs.current[current];
    if (!viewport || !el) return;
    const vw = viewport.clientWidth;
    const ew = el.clientWidth;
    const left = el.offsetLeft; // track 内での位置（transformの影響を受けない）
    const next = left - (vw - ew) / 2;
    setTranslatePx(next);
  };

  useEffect(() => {
    // 目的: current 変更後にDOM計測して中央寄せする
    if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null;
      try {
        measureAndSetTranslate();
      } catch (e) {
        console.error('[ギャラリー: 位置計算エラー]', e);
      }
    });
    return () => {
      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [current, loopItems.length]);

  useEffect(() => {
    // 目的: リサイズ時も中央寄せを保つ
    const onResize = () => {
      try {
        measureAndSetTranslate();
      } catch (e) {
        console.error('[ギャラリー: リサイズ同期エラー]', e);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (len === 0) {
    return (
      <div className='flex w-full aspect-video items-center justify-center rounded-none border border-gray-200 bg-white text-sm text-gray-500'>
        まだギャラリー画像がありません（管理画面で設定できます）
      </div>
    );
  }

  const realIdx = hasLoop ? ((current - CLONE_COUNT + len) % len) : 0;
  const goPrev = () => {
    if (!hasLoop) return;
    if (animatingRef.current) return;
    animatingRef.current = true;
    setCurrent((p) => p - 1);
  };
  const goNext = () => {
    if (!hasLoop) return;
    if (animatingRef.current) return;
    animatingRef.current = true;
    setCurrent((p) => p + 1);
  };
  const goTo = (realIndex: number) => {
    if (!hasLoop) return;
    if (animatingRef.current) return;
    animatingRef.current = true;
    setCurrent(realIndex + CLONE_COUNT);
  };

  return (
    // NOTE: 親がflexのときでも幅が潰れないよう w-full を明示する
    <div className='relative w-full overflow-hidden rounded-none border border-gray-200 bg-white shadow-sm'>
      {/* 目的: 手動スクロール不可＋無限ループのため、スクロールではなく transform で移動する */}
      <div className='relative bg-gray-50 px-6 py-6'>
        <div ref={viewportRef} className='overflow-hidden'>
          <div
            ref={trackRef}
            className={[
              'flex gap-4',
              enableTransition ? 'transition-transform duration-500 ease-out' : 'transition-none',
            ].join(' ')}
            style={{ transform: `translateX(-${translatePx}px)` }}
            onTransitionEnd={() => {
              if (!hasLoop) return;
              // 目的: クローン位置へ到達したら、実アイテム位置へ「瞬間移動」して無限ループを成立させる
              // 左クローン領域: 0..CLONE_COUNT-1（末尾側のコピー）
              // 右クローン領域: CLONE_COUNT+len .. CLONE_COUNT+len+CLONE_COUNT-1（先頭側のコピー）
              const firstReal = CLONE_COUNT;
              const lastReal = CLONE_COUNT + len - 1;
              const leftCloneMax = CLONE_COUNT - 1;
              const rightCloneMin = CLONE_COUNT + len;

              if (current <= leftCloneMax) {
                setEnableTransition(false);
                setCurrent(lastReal);
              } else if (current >= rightCloneMin) {
                setEnableTransition(false);
                setCurrent(firstReal);
              }
              animatingRef.current = false;
            }}
          >
            {loopItems.map((it, i) => (
              <div
                key={`${it.id}-${i}`}
                ref={(el) => {
                  slideRefs.current[i] = el;
                }}
                className='shrink-0 w-[78%] sm:w-[62%] lg:w-[48%]'
              >
                <div className='group relative overflow-hidden rounded-none border border-gray-200 bg-white shadow-sm'>
                  <div className='relative aspect-[16/10] bg-gray-100'>
                    <Image
                      src={it.url}
                      alt={it.altText ?? ''}
                      fill
                      className='object-cover transition-transform duration-500 group-hover:scale-[1.02]'
                      sizes='(min-width: 1024px) 560px, (min-width: 640px) 70vw, 90vw'
                      quality={90}
                      // NOTE: 先頭付近を優先（体感速度改善）
                      priority={i <= 2}
                    />
                    <div className='pointer-events-none absolute inset-0 bg-linear-to-b from-black/0 via-black/0 to-black/10' />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Controls */}
      {hasLoop ? (
        <>
          <button
            type='button'
            onClick={goPrev}
            className='absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 px-3 py-2 text-sm font-semibold text-gray-700 shadow hover:bg-white'
            aria-label='前へ'
          >
            ‹
          </button>
          <button
            type='button'
            onClick={goNext}
            className='absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 px-3 py-2 text-sm font-semibold text-gray-700 shadow hover:bg-white'
            aria-label='次へ'
          >
            ›
          </button>

          <div className='absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-2'>
            {safeItems.map((it, i) => (
              <button
                key={it.id}
                type='button'
                onClick={() => goTo(i)}
                className={`h-2.5 w-2.5 rounded-full ${
                  i === realIdx ? 'bg-white' : 'bg-white/50 hover:bg-white/80'
                }`}
                aria-label={`スライド ${i + 1}`}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
